import { useCallback, useEffect, useRef, useState } from "react";
import http from "../utils/http-common";

const RENEW_MS = 60_000;

/**
 * Pessimistic admin edit lock for a partner dossier: acquire on mount, renew, release on unmount.
 * @param {string|null|undefined} partnerId
 * @param {boolean} enabled — viewer is admin and partner is loaded
 */
export function usePartnerEditLock(partnerId, enabled) {
  const [editLockToken, setEditLockToken] = useState(null);
  const [partnerDossierLockedByPeer, setPartnerDossierLockedByPeer] = useState(false);
  const [lockHolderUserId, setLockHolderUserId] = useState(null);
  const [lockSessionExpired, setLockSessionExpired] = useState(false);

  const tokenRef = useRef(null);
  const renewTimerRef = useRef(null);
  const enabledRef = useRef(enabled);
  const partnerIdRef = useRef(partnerId);

  enabledRef.current = enabled;
  partnerIdRef.current = partnerId;

  const clearRenewTimer = () => {
    if (renewTimerRef.current) {
      clearInterval(renewTimerRef.current);
      renewTimerRef.current = null;
    }
  };

  const scheduleRenewal = useCallback(() => {
    clearRenewTimer();
    renewTimerRef.current = setInterval(() => {
      const pid = partnerIdRef.current;
      const tok = tokenRef.current;
      if (!pid || !tok || !enabledRef.current) return;
      http
        .patch(`/api/v1/medical-partners/${pid}/edit-lock`, {}, {
          headers: { "X-Partner-Edit-Lock": tok },
          suppressInlineErrorSwal: true,
        })
        .then(() => setLockSessionExpired(false))
        .catch((e) => {
          const st = e.response?.status;
          if (st === 409 || st === 403) {
            tokenRef.current = null;
            setEditLockToken(null);
            setLockSessionExpired(true);
          }
        });
    }, RENEW_MS);
  }, []);

  useEffect(() => {
    if (!enabled || !partnerId) {
      clearRenewTimer();
      tokenRef.current = null;
      setEditLockToken(null);
      setPartnerDossierLockedByPeer(false);
      setLockHolderUserId(null);
      setLockSessionExpired(false);
      return undefined;
    }

    let cancelled = false;

    const release = () => {
      const pid = partnerId;
      const tok = tokenRef.current;
      if (!pid || !tok) return;
      http
        .delete(`/api/v1/medical-partners/${pid}/edit-lock`, {
          headers: { "X-Partner-Edit-Lock": tok },
          suppressInlineErrorSwal: true,
        })
        .catch(() => {});
    };

    (async () => {
      try {
        const r = await http.post(
          `/api/v1/medical-partners/${partnerId}/edit-lock`,
          {},
          { suppressInlineErrorSwal: true }
        );
        const d = r.data?.data;
        if (cancelled || !d?.lockToken) return;
        tokenRef.current = d.lockToken;
        setEditLockToken(d.lockToken);
        setPartnerDossierLockedByPeer(false);
        setLockHolderUserId(null);
        setLockSessionExpired(false);
        scheduleRenewal();
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 409) {
          setPartnerDossierLockedByPeer(true);
          setLockHolderUserId(e.response?.data?.holderUserId || null);
          tokenRef.current = null;
          setEditLockToken(null);
        } else {
          tokenRef.current = null;
          setEditLockToken(null);
        }
      }
    })();

    return () => {
      cancelled = true;
      clearRenewTimer();
      release();
      tokenRef.current = null;
    };
  }, [partnerId, enabled, scheduleRenewal]);

  const takeoverEditLock = useCallback(async () => {
    if (!partnerId) return false;
    try {
      const r = await http.post(
        `/api/v1/medical-partners/${partnerId}/edit-lock`,
        { force: true },
        { suppressInlineErrorSwal: true }
      );
      const d = r.data?.data;
      if (d?.lockToken) {
        clearRenewTimer();
        tokenRef.current = d.lockToken;
        setEditLockToken(d.lockToken);
        setPartnerDossierLockedByPeer(false);
        setLockHolderUserId(null);
        setLockSessionExpired(false);
        scheduleRenewal();
        return true;
      }
    } catch {
      return false;
    }
    return false;
  }, [partnerId, scheduleRenewal]);

  return {
    editLockToken,
    partnerDossierLockedByPeer,
    lockHolderUserId,
    lockSessionExpired,
    takeoverEditLock,
  };
}
